import { createSelector } from '@reduxjs/toolkit';

import { selectProductPrice } from 'src/redux/product/selectors';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import { imageOptionBounds } from 'src/utils/imageOptionBounds';
import { countImagesForImageOptionLimit } from 'src/utils/imageOptionWireFormat';
import type { ReduxApplicationState } from '../combinedReducer';
import { getOptionsBreakdown } from './helpers/selectorHelpers';

export const selectOptions = (state: ReduxApplicationState) => state.basket.options;

export const selectIsSubmitOptionsFormPending = (state: ReduxApplicationState) =>
  state.basket.isPending;

export const selectSelectedOption = (state: ReduxApplicationState, optionId: number) =>
  state.basket.selectedOptionValues[optionId];

export const selectSelectedOptionValues = (state: ReduxApplicationState) =>
  state.basket.selectedOptionValues;

export const selectTrackedEvents = (state: ReduxApplicationState) => state.basket.trackedEvents;

// We're in edit mode once the hydration thunk has resolved the `editItem` id to
// a real basket item (setting `editedItem`); until then (and for a bogus id)
// `editedItem` stays null and we're in add mode.
export const selectIsInEditMode = (state: ReduxApplicationState) =>
  state.basket.editedItem !== null;

export const selectOptionsBreakdown = createSelector(
  [selectSelectedOptionValues, selectProductPrice],
  (selections, { currency }) => getOptionsBreakdown(selections, currency),
);

export const selectSelectedOptionsTotal = createSelector(
  [selectOptionsBreakdown],
  ({ amountAdded, amountRemoved }) => amountAdded + amountRemoved,
);

export const selectTotalPrice = createSelector(
  [selectSelectedOptionsTotal, selectProductPrice],
  (optionsTotalAmount, productPrice) => optionsTotalAmount + productPrice.amount,
);

export const selectInvalidCustomOptionValues = createSelector(
  [selectSelectedOptionValues],
  (selections) =>
    Object.values(selections || {})
      .flat()
      .filter(
        (value?: CatServiceAPIProductOptionValue) =>
          !!value?.isCustomInput && value?.customOptionKind !== 'image' && !value?.isValid,
      ),
);

export const selectOutstandingOptions = createSelector(
  [selectSelectedOptionValues, selectOptions],
  (selections, personalisationOptions) =>
    personalisationOptions.filter((opt) => {
      if (!opt.required) {
        return false;
      }
      if (opt.type === 'image') {
        const { max, min } = imageOptionBounds(opt);
        const minCount = opt.required ? Math.max(min, 1) : min;
        const n = countImagesForImageOptionLimit(selections[opt.id]?.[0]);
        return n < minCount || n > max;
      }
      return !selections[opt.id]?.length;
    }),
);

export const selectNumberOfRequiredOptions = createSelector([selectOptions], (options) =>
  options.reduce((count, opt) => (opt.required ? count + 1 : count), 0),
);

export const selectAreRequiredOptionsIncomplete = createSelector(
  [selectOutstandingOptions, selectInvalidCustomOptionValues],
  (outstanding, invalidCustom) => outstanding.length > 0 || invalidCustom.length > 0,
);
